import { ArrowDown, ArrowUp, Calendar, Check, ChevronDown, ChevronRight, Columns3, Hash, Inbox, Lightbulb, List as ListIcon, Mail, Mic, Plus, RefreshCw, Rows3, Search, Sprout, Upload, UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AGENCY_TZ, api, formatDate } from "../api";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  monogram,
  PageHeader,
} from "../components/Ui";
import Dialog from "../components/Dialog";
import FilterPill from "../components/FilterPill";
import QuickMenu, { QuickMenuItem } from "../components/QuickMenu";
import { TASK_STATUS_LABEL, type Client, type Task } from "../types";
import { cleanTitle } from "../task-title";
import { FitScore, IdeaReview } from "../components/WorkIdeas";

type TaskRecord = Task & {
  notes?: string;
  assigned_to?: number;
  deep_link?: string;
  snooze_until?: string;
  /** Hand-set place in its status group; null until someone arranges it. */
  sort_order?: number | null;
  /** The meeting a meeting_action_item came from (task detail only). */
  meeting_title?: string | null;
  meeting_date?: string | null;
  meeting_transcript_url?: string | null;
  created_at?: string | null;
  auto_generated?: boolean;
};
const states = ["pending", "in_progress", "completed", "snoozed", "escalated"];
/** "Jul 14" on the agency's calendar (US Eastern), plus the year when it is not this year. */
function createdOn(iso: string | null | undefined) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const year = (x: Date) => new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: AGENCY_TZ }).format(x);
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: AGENCY_TZ, ...(year(d) !== year(new Date()) ? { year: "numeric" } : {}) }).format(d);
}
/** A client request's body: a small Type/From header above what the client wrote (utils/clientRequests.js). */
function parseRequest(body: string | null | undefined) {
  const text = String(body || "");
  const m = /^((?:(?:Type|From): [^\n]*\n?)+)\n([\s\S]*)$/.exec(text);
  if (!m) return { type: null as string | null, from: null as string | null, details: text };
  const line = (key: string) => new RegExp(`^${key}: (.*)$`, "m").exec(m[1])?.[1]?.trim() || null;
  return { type: line("Type"), from: line("From"), details: m[2] };
}
/**
 * Where a task came from, for the editor's "Created Jul 14 ..." line. Tasks carry
 * no creator column, so this reads the source the row does carry: its type, the
 * page it links to, or the client's name on a portal request.
 */
function origin(t: TaskRecord) {
  if (t.meeting_title) return `from the meeting “${t.meeting_title}”${t.meeting_date ? ` on ${formatDate(t.meeting_date)}` : ""}`;
  switch (t.task_type) {
    case "client_request": {
      const from = parseRequest(t.body).from?.replace(/\s*\([^)]*\)\s*$/, "");
      return from && from !== "Portal link" ? `by ${from} in the client portal` : "in the client portal";
    }
    case "slack_action_item": return "from a Slack message";
    case "email_action_item": return "from an email";
    case "meeting_action_item": return "from a Fireflies meeting";
    case "client_contact": return "by the client contact check";
    case "approval_review": return "by the approval check";
    case "call_follow_up": return "from a tracked call";
    case "lead_follow_up": return "from a new lead";
    case "ghl_suggestion": return "from a GoHighLevel suggestion";
    case "removal_follow_up": return "from a review removal request";
    case "performance_check": return "by the performance check";
  }
  const link = t.deep_link || "";
  if (link.startsWith("/ads-review")) return "from the Weekly ads review";
  if (link.startsWith("/crm/")) return "from the CRM";
  return t.auto_generated ? "automatically" : "";
}
const EDITOR_STATUS = TASK_STATUS_LABEL;
const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
function dueBeforeToday(value: string | null) {
  if (!value) return false;
  const today = new Date();
  const day = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  return value.slice(0, 10) < day;
}
export function TaskEditor({
  id,
  onClose,
  onSaved,
  onDeleted,
  defaultClientId,
  defaultKind = "general",
}: {
  id: string;
  onClose: () => void;
  onSaved: () => void;
  /** After a delete; without it the editor calls onSaved, which reloads the list. */
  onDeleted?: (task: TaskRecord) => void;
  /** New tasks start on this client (the queue is filtered to it). */
  defaultClientId?: string | null;
  defaultKind?: "general" | "idea";
}) {
  const existing = useApi<TaskRecord>(id === "new" ? null : `/api/tasks/${id}`);
  const people = useApi<Array<{ id: number; full_name: string }>>(
    "/api/tasks/assignees",
  );
  const clients = useApi<Client[]>("/api/clients");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [draftChanged, setDraftChanged] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const task = id === "new" ? null : existing.data;
  const idea = task ? task.task_type === "idea" : defaultKind === "idea";
  const shownStatus = status ?? task?.status ?? "pending";
  const clientLinkTo = task?.client_id ? `/clients/${task.client_id}` : null;
  const related = relatedLink(task?.deep_link, task?.client_id);
  // A client request's body is what the client wrote, and the portal shows it
  // back to them, so it reads as the request and the team writes in the notes,
  // which the client never sees. Any other task has one Details field; notes
  // left on it from before show underneath, read-only, so no text is lost.
  const request = task && isClientRequest(task) ? parseRequest(task.body) : null;
  const oldNotes = !request && task?.notes?.trim() ? task.notes : "";
  const madeFrom = task ? origin(task) : "";
  async function remove() {
    if (!task) return;
    setBusy(true);
    setError("");
    try {
      await api(`/api/tasks/${task.id}`, { method: "DELETE" });
      window.dispatchEvent(new Event("nl-aos-tasks-changed"));
      if (onDeleted) onDeleted(task);
      else onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete the task");
      setConfirmDelete(false);
      setBusy(false);
    }
  }
  async function save(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError("");
    const fields = Object.fromEntries(new FormData(e.currentTarget));
    try {
      await api(id === "new" ? "/api/tasks" : `/api/tasks/${id}`, {
        method: id === "new" ? "POST" : "PUT",
        body: JSON.stringify({
          ...fields,
          ...(id === "new" ? { task_type: defaultKind } : {}),
          assigned_to: fields.assigned_to ? Number(fields.assigned_to) : null,
          client_id: fields.client_id ? Number(fields.client_id) : null,
          // The field holds the viewer's own wall time; send the exact instant so the
          // server never reads it in its own time zone (snoozes woke 4 hours early).
          ...("snooze_until" in fields ? { snooze_until: fields.snooze_until ? new Date(String(fields.snooze_until)).toISOString() : null } : {}),
        }),
      });
      window.dispatchEvent(new Event('nl-aos-tasks-changed'));
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save task");
    } finally {
      setBusy(false);
    }
  }
  async function promote() {
    if (!task || busy) return;
    setBusy(true); setError("");
    try {
      await api(`/api/tasks/${task.id}/promote`, { method: "POST", body: "{}" });
      window.dispatchEvent(new Event("nl-aos-tasks-changed"));
      onSaved();
    } catch (e) { setError(e instanceof Error ? e.message : "The idea could not be moved."); }
    finally { setBusy(false); }
  }
  return (
    <Dialog
      title={id === "new" ? (idea ? "New idea" : "New task") : (idea ? "Idea details" : "Task details")}
      description={task && isClientRequest(task) ? `Client request${task.client_name ? ` from ${task.client_name}` : ""}, sent from their portal. They see its status there.` : task?.client_name || undefined}
      onClose={() => { if (!busy) onClose(); }}
    >
      {existing.loading ? (
        <LoadingState label="Loading task" />
      ) : existing.error ? (
        <ErrorState message={existing.error} onRetry={existing.reload} />
      ) : (
        <form className="operation-form" onSubmit={save} onChange={e => {
          const field = e.target;
          if ((field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) && field.name) setDraftChanged(true);
        }}>
          <label>
            Title
            <input
              name="title"
              required
              maxLength={500}
              defaultValue={task?.title ? cleanTitle(task.title) : undefined}
              autoFocus
            />
          </label>
          {(clientLinkTo || related) && (
            <p className="task-links">
              {clientLinkTo && <Link to={clientLinkTo}>Open client workspace</Link>}
              {related && related !== clientLinkTo && <Link to={related}>Open related work</Link>}
            </p>
          )}
          {request ? (
            <div className="task-readonly">
              <span className="task-readonly-label">Request{request.type ? ` (${request.type})` : ""}</span>
              <p>{request.details.trim() || "No details were sent."}</p>
            </div>
          ) : (
            <label>
              Details
              <textarea name="body" rows={idea ? 8 : 4} maxLength={idea ? 40000 : undefined} defaultValue={(task?.body || "").replace(/has not been contacted in 999 days/i, "has no recorded contact yet")} />
            </label>
          )}
          {request && (
            <label>
              Internal notes
              <textarea name="notes" rows={3} defaultValue={task?.notes || ""} placeholder="The client does not see these" />
            </label>
          )}
          {id === "new" && (
            <label>
              Client
              <select name="client_id" defaultValue={defaultClientId || ""}>
                <option value="">Agency</option>
                {clients.data?.map((c) => (
                  <option value={c.id} key={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          {(() => {
            const ownerField = (
              <label>
                Owner
                <select name="assigned_to" defaultValue={task?.assigned_to || ""}>
                  <option value="">
                    {id === "new" ? "Assign to me" : "Unassigned"}
                  </option>
                  {people.data?.map((p) => (
                    <option value={p.id} key={p.id}>
                      {p.full_name}
                    </option>
                  ))}
                </select>
              </label>
            );
            const dueField = (
              <label>
                Due date
                <input
                  name="due_date"
                  type="date"
                  defaultValue={task?.due_date?.slice(0, 10) || ""}
                />
              </label>
            );
            const priorityField = (
              <label>
                Priority
                <select name="priority" defaultValue={task?.priority || "medium"}>
                  {["low", "medium", "high", "critical"].map((s) => (
                    <option key={s} value={s}>{capital(s)}</option>
                  ))}
                </select>
              </label>
            );
            // A new task pairs priority with the date and gives the owner its own row;
            // an existing one pairs owner with date and priority with status.
            return id === "new" ? (
              <>
                <div className="form-pair">{priorityField}{dueField}</div>
                {ownerField}
              </>
            ) : (
              <>
                <div className="form-pair">{ownerField}{dueField}</div>
                <div className="form-pair">
                  {priorityField}
                  <label>
                    Status
                    <select name="status" value={shownStatus} onChange={(e) => setStatus(e.target.value)}>
                      {states.map((s) => (
                        <option key={s} value={s}>
                          {EDITOR_STATUS[s] || s.replaceAll("_", " ")}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </>
            );
          })()}
          {id !== "new" && shownStatus === "snoozed" && (
            <label>
              Snooze until
              <input
                name="snooze_until"
                type="datetime-local"
                required
                defaultValue={localInput(task?.snooze_until)}
              />
            </label>
          )}
          {oldNotes && (
            <div className="task-readonly">
              <span className="task-readonly-label">Notes</span>
              <p>{oldNotes}</p>
            </div>
          )}
          {task && (
            // Where the task came from, and Delete, sit quietly below the fields, away from Save.
            <div className="task-meta">
              <p className="task-created">
                {task.created_at ? `Created ${createdOn(task.created_at)}${madeFrom ? ` ${madeFrom}` : ""}` : madeFrom ? `Created ${madeFrom}` : ""}
                {task.meeting_transcript_url?.startsWith("https://") && (
                  <>. <a href={task.meeting_transcript_url} target="_blank" rel="noreferrer">Open the recording</a></>
                )}
              </p>
              {confirmDelete ? (
                <span className="task-delete-confirm" role="group" aria-label="Confirm delete">
                  <span>{request ? "Delete it? The client will no longer see it." : "Delete this task?"}</span>
                  <button type="button" className="button small danger" disabled={busy} onClick={() => void remove()}>{busy ? "Deleting…" : "Delete"}</button>
                  <button type="button" className="button small secondary" disabled={busy} onClick={() => setConfirmDelete(false)}>Keep</button>
                </span>
              ) : (
                <button type="button" className="text-button task-delete" disabled={busy} onClick={() => setConfirmDelete(true)}>Delete task</button>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="inline-notice danger">
              {error}
            </p>
          )}
          {idea && task?.client_id && <IdeaReview taskId={task.id} clientId={Number(task.client_id)} draftChanged={draftChanged} />}
          <div className="operation-actions">
            {idea && task && <button type="button" className="button secondary" disabled={busy || draftChanged} onClick={() => void promote()}>Turn into task</button>}
            <button
              className="button secondary"
              type="button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button className="button" disabled={busy}>
              {busy && !confirmDelete ? "Saving…" : idea ? "Save idea" : "Save task"}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
type View = "list" | "table" | "board";
const VIEW_KEY = "nl-aos-queue-view";
const isView = (v: unknown): v is View => v === "list" || v === "table" || v === "board";
const STATUS_LABEL: Record<string, string> = {
  pending: "To do",
  in_progress: "In progress",
  escalated: "Escalated",
  snoozed: "Snoozed",
  completed: "Done",
};
const BOARD: string[] = ["pending", "in_progress", "escalated", "snoozed", "completed"];
const PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
/**
 * ?priority= as a clean value: a comma list of priorities, highest first
 * ("critical,high"), or "urgent", which is the Agency overview's count: still To do,
 * and critical, high or a client request (GET /api/tasks/summary "attention").
 */
function parsePriority(value: string | null) {
  const parts = (value || "").toLowerCase().split(",").map((s) => s.trim());
  if (parts.includes("urgent")) return "urgent";
  return [...new Set(parts.filter((p) => p in PRIORITY_RANK))].sort((a, b) => PRIORITY_RANK[a] - PRIORITY_RANK[b]).join(",");
}
function matchesPriority(t: { priority: string; status: string; task_type?: string }, priority: string) {
  if (!priority) return true;
  if (priority === "urgent") return t.status === "pending" && (t.priority === "critical" || t.priority === "high" || t.task_type === "client_request");
  return priority.split(",").includes(t.priority);
}
/** "Critical and high", "Critical, high and low". */
function priorityName(priority: string) {
  if (priority === "urgent") return "Urgent";
  const names = priority.split(",").map((p, i) => (i ? p : p.charAt(0).toUpperCase() + p.slice(1)));
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "Any";
}
const PRIORITY_OPTIONS = ["", "urgent", "critical,high", "critical", "high", "medium", "low"];
/** Links older generators wrote to pages that never existed, pointed at the real page. */
function relatedLink(link: string | null | undefined, clientId: number | null | undefined) {
  if (!link?.startsWith("/") || link.startsWith("//")) return null;
  const approval = /^\/approvals\/(\d+)$/.exec(link);
  if (approval) return `/approvals?record=${approval[1]}`;
  if (/^\/reviews\/\d+$/.test(link)) return clientId ? `/reputation?client_id=${clientId}` : null;
  const ghl = /^\/clients\/(\d+)\/ghl$/.exec(link);
  if (ghl) return `/clients/${ghl[1]}`;
  return link;
}
/** An ISO instant as the value a datetime-local field shows: the viewer's own clock. */
function localInput(iso: string | null | undefined) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
const isClientRequest = (t: { task_type?: string }) => t.task_type === "client_request";
/** Where a task came from, wherever it shows: the client's portal, Slack, an email or the SEO plan. */
type IdeaFit = { id: number; score: number | null; verdict: string | null; evaluating: boolean; client_visible: boolean };
function RequestChip({ t, fit }: { t: { task_type?: string }; fit?: IdeaFit }) {
  if (t.task_type === "idea") return fit ? <>{fit.client_visible && <span className="chip chip-sm chip-request" title="Shared by the client from their portal"><Inbox aria-hidden="true" />From client</span>}{fit.evaluating && !fit.score ? <span className="idea-fit">Reviewing…</span> : <FitScore score={fit.score} verdict={fit.verdict} />}</> : null;
  if (isClientRequest(t)) return <span className="chip chip-sm chip-request" title="Sent by the client from their portal"><Inbox aria-hidden="true" />Client request</span>;
  if (t.task_type === "slack_action_item") return <span className="chip chip-sm chip-source" title="Made from a message in the client's Slack channel"><Hash aria-hidden="true" />Slack</span>;
  if (t.task_type === "email_action_item") return <span className="chip chip-sm chip-source" title="Made from an email in the connected inbox"><Mail aria-hidden="true" />Email</span>;
  if (t.task_type === "seo") return <span className="chip chip-sm chip-source" title="From this week's SEO plan"><Sprout aria-hidden="true" />SEO plan</span>;
  return null;
}
const initials = monogram;
const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

function Owner({ name }: { name: string | null | undefined }) {
  return name ? (
    <span className="q-avatar" title={name}>{initials(name)}</span>
  ) : (
    <span className="q-avatar empty" title="Unassigned" />
  );
}
function Priority({ value }: { value: string }) {
  return <span className={`q-priority p-${value}`}><i aria-hidden="true" />{value}</span>;
}
/** "Sep 14", plus the year only when it is not this year. */
function shortDue(value: string) {
  const d = new Date(`${value.slice(0, 10)}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(d.getFullYear() !== new Date().getFullYear() ? { year: "numeric" } : {}) });
}
/**
 * A board card's date. No date shows nothing, as in the list and table. Overdue is the
 * red date, as in the list's chip, so priority, date and owner fit on one line.
 */
function Due({ task }: { task: TaskRecord }) {
  if (!task.due_date) return null;
  const late = task.status !== "completed" && dueBeforeToday(task.due_date);
  return (
    <time className={late ? "q-due late" : "q-due"} dateTime={task.due_date} title={late ? `Overdue since ${formatDate(task.due_date)}` : undefined}>
      {late && <span className="sr-only">Overdue, </span>}
      {shortDue(task.due_date)}
    </time>
  );
}

type Person = { id: number; full_name: string };
/** True while the viewport matches `query`; the table view hands over to the list on phones. */
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => (typeof window !== "undefined" ? window.matchMedia(query).matches : false));
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = () => setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}
type Fields = Partial<Omit<TaskRecord, "assigned_to" | "due_date">> & { assigned_to?: number | null; due_date?: string | null };
type Patch = (t: TaskRecord, fields: Fields) => unknown;
const PRIORITIES = ["critical", "high", "medium", "low"] as const;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** YYYY-MM-DD for today plus `days`, in the browser's calendar. */
function isoOffset(days: number) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// ── Inline chips (Orbit-style): click, pick, the row patches in place. ──

function DueChip({ t, onPatch }: { t: TaskRecord; onPatch: Patch }) {
  const late = t.status !== "completed" && dueBeforeToday(t.due_date);
  const trigger = t.due_date ? (
    <span className={`chip q-due-chip${late ? " chip-danger" : " chip-ghost"}`} title={late ? `Overdue since ${formatDate(t.due_date)}` : undefined}>
      <Calendar aria-hidden="true" />{shortDue(t.due_date)}
    </span>
  ) : (
    <span className="chip chip-ghost chip-empty q-due-chip"><Calendar aria-hidden="true" />Add date</span>
  );
  return (
    <QuickMenu label={`Due date for ${cleanTitle(t.title)}`} trigger={trigger}>
      {(close) => (
        <div className="qmenu-date">
          <DueField t={t} onPatch={onPatch} close={close} />
          <div className="qmenu-date-quick">
            <button type="button" onClick={() => { void onPatch(t, { due_date: isoOffset(0) }); close(); }}>Today</button>
            <button type="button" onClick={() => { void onPatch(t, { due_date: isoOffset(1) }); close(); }}>Tomorrow</button>
            <button type="button" onClick={() => { void onPatch(t, { due_date: isoOffset(7) }); close(); }}>Next week</button>
            {t.due_date && <button type="button" className="danger" onClick={() => { void onPatch(t, { due_date: null }); close(); }}>Clear</button>}
          </div>
        </div>
      )}
    </QuickMenu>
  );
}

/**
 * A typed date saves when you leave the field or press Enter, never per key:
 * typing a year fires a change on every digit (0002, 0020, 0202, 2026), and
 * each one used to save, out of order.
 */
function DueField({ t, onPatch, close }: { t: TaskRecord; onPatch: Patch; close: () => void }) {
  const [draft, setDraft] = useState(t.due_date?.slice(0, 10) || "");
  const commit = () => {
    const current = t.due_date?.slice(0, 10) || "";
    if (draft === current) return false;
    if (draft && !/^(19|20)\d\d-\d\d-\d\d$/.test(draft)) return false;
    void onPatch(t, { due_date: draft || null });
    return true;
  };
  return (
    <label className="qmenu-date-field">
      <span>Due</span>
      <input
        type="date"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { commit(); }}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); close(); } }}
      />
    </label>
  );
}

function StatusChip({ t, onMove }: { t: TaskRecord; onMove: (t: TaskRecord, to: string) => unknown }) {
  return (
    <QuickMenu label={`Status of ${cleanTitle(t.title)}`} trigger={
      <span className="chip chip-ghost"><span className={`q-dot s-${t.status}`} aria-hidden="true" />{STATUS_LABEL[t.status] || t.status}<ChevronDown aria-hidden="true" /></span>
    }>
      {(close) => BOARD.map((s) => (
        <QuickMenuItem key={s} active={t.status === s} onClick={() => { void onMove(t, s); close(); }}>
          <span className={`q-dot s-${s}`} aria-hidden="true" />{STATUS_LABEL[s]}
        </QuickMenuItem>
      ))}
    </QuickMenu>
  );
}

function PriorityChip({ t, onPatch }: { t: TaskRecord; onPatch: Patch }) {
  return (
    <QuickMenu label={`Priority of ${cleanTitle(t.title)}`} align="right" trigger={<span className="chip chip-ghost"><Priority value={t.priority} /></span>}>
      {(close) => PRIORITIES.map((p) => (
        <QuickMenuItem key={p} active={t.priority === p} onClick={() => { void onPatch(t, { priority: p }); close(); }}>
          <Priority value={p} />
        </QuickMenuItem>
      ))}
    </QuickMenu>
  );
}

/** `named`: list rows also show the owner's first name, when the row has room (work.css). */
function OwnerChip({ t, people, onPatch, named = false }: { t: TaskRecord; people: Person[]; onPatch: Patch; named?: boolean }) {
  const avatar = t.assigned_to_name
    ? <span className="chip-owner" title={t.assigned_to_name}><Owner name={t.assigned_to_name} /></span>
    : <span className="chip-owner empty" title="Assign"><UserPlus aria-hidden="true" /></span>;
  return (
    <QuickMenu label={`Owner of ${cleanTitle(t.title)}`} align="right" trigger={
      named
        ? <span className="q-owner-chip" title={t.assigned_to_name || "Assign"}>{avatar}{t.assigned_to_name && <span className="q-owner-first" aria-hidden="true">{firstName(t.assigned_to_name)}</span>}</span>
        : avatar
    }>
      {(close) => (
        <>
          <QuickMenuItem active={!t.assigned_to} onClick={() => { void onPatch(t, { assigned_to: null, assigned_to_name: null }); close(); }}>
            <span className="q-avatar empty" aria-hidden="true" />Unassigned
          </QuickMenuItem>
          {people.map((p) => (
            <QuickMenuItem key={p.id} active={t.assigned_to === p.id} onClick={() => { void onPatch(t, { assigned_to: p.id, assigned_to_name: p.full_name }); close(); }}>
              <span className="q-avatar" aria-hidden="true">{initials(p.full_name)}</span>{p.full_name}
            </QuickMenuItem>
          ))}
        </>
      )}
    </QuickMenu>
  );
}

/** Module scope on purpose: defined inside the page, every render made a new
 * component type, remounting each button and dropping keyboard focus. */
function CheckButton({ t, onToggle }: { t: TaskRecord; onToggle: (t: TaskRecord) => unknown }) {
  return (
    <button
      type="button"
      className={t.status === "completed" ? "q-check done" : "q-check"}
      aria-label={t.task_type === "idea" ? `Open idea ${cleanTitle(t.title)}` : t.status === "completed" ? `Reopen ${cleanTitle(t.title)}` : `Complete ${cleanTitle(t.title)}`}
      onClick={(e) => { e.stopPropagation(); void onToggle(t); }}
    >
      {t.task_type === "idea" ? <Lightbulb aria-hidden="true" /> : <Check aria-hidden="true" />}
    </button>
  );
}

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const narrow = useMediaQuery("(max-width: 640px)");
  const { user } = useAuth();
  // A superadmin sees everyone's work and can narrow it by owner; anyone else sees
  // only the tasks assigned to them (routes/tasks.js), so the owner filter and the
  // "Assigned to me" view would say nothing new.
  const admin = user?.role === "superadmin";
  const chosenView: View = (() => {
    const fromUrl = params.get("view");
    if (isView(fromUrl)) return fromUrl;
    try { const v = localStorage.getItem(VIEW_KEY); if (isView(v)) return v; } catch { /* private mode */ }
    return "list";
  })();
  // Seven columns do not fit a phone; the list carries the same chips, so it stands in.
  const view: View = chosenView === "table" && narrow ? "list" : chosenView;
  // The board and the list show every status (as columns / groups), so only
  // the table filters by one.
  const status = view === "table" ? params.get("status") || "pending" : "all";
  const clientFilter = params.get("client_id");
  const focus = [...(admin ? ["mine"] : []), "overdue", "requests", "ideas"].includes(params.get("focus") || "") ? params.get("focus")! : "all";
  // ?owner=<user id> (or "none" for unassigned) and ?priority=critical,high (or
  // "urgent") are links from Team and the Agency overview; both show as pills.
  const owner = admin && /^(\d+|none)$/.test(params.get("owner") || "") ? params.get("owner")! : "";
  const priority = parsePriority(params.get("priority"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: "title" | "client" | "owner" | "status" | "priority" | "due"; dir: 1 | -1 }>({ key: "priority", dir: 1 });
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ completed: true });
  const [dragging, setDragging] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // Where a dragged row would land in the list: above or below this task.
  const [rowDrop, setRowDrop] = useState<{ id: number; after: boolean } | null>(null);
  const [moved, setMoved] = useState<Record<number, string>>({});
  const [notice, setNotice] = useState("");
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  // Every view loads the same list once: open work plus the last 14 days of done
  // (status=current). Views, status and the filters narrow it in the browser, so
  // switching List, Table or Board never refetches. "Show older done tasks" loads
  // everything; the previous list stays on screen while it does.
  const [olderDone, setOlderDone] = useState(false);
  const request = useApi<TaskRecord[]>(
    `/api/tasks?status=${olderDone ? "all" : "current"}${clientFilter ? `&client_id=${encodeURIComponent(clientFilter)}` : ""}`,
    { keepPrevious: true },
  );
  const clients = useApi<Client[]>(clientFilter ? "/api/clients" : null);
  const filterClientName = clientFilter
    ? clients.data?.find((c) => String(c.id) === clientFilter)?.name || request.data?.find((t) => String(t.client_id) === clientFilter)?.client_name || "This client"
    : null;
  const people = useApi<Person[]>("/api/tasks/assignees");
  // Ideas carry their AI fit score; the plain task list does not, so the Ideas view reads it here.
  const ideaRows = useApi<IdeaFit[]>(focus === "ideas" ? "/api/work-ideas" : null);
  const fits = useMemo(() => new Map((ideaRows.data || []).map((r) => [r.id, r])), [ideaRows.data]);
  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    value === null ? next.delete(key) : next.set(key, value);
    setParams(next);
  };
  // Filters replace the history entry: Back leaves the page, not each pill change.
  const setFilter = (key: "owner" | "priority" | "status" | "client_id", value: string | null) => {
    const next = new URLSearchParams(params);
    value ? next.set(key, value) : next.delete(key);
    setParams(next, { replace: true });
  };
  const open = (id?: string) => setParam("task", id ?? null);
  const setView = (v: View) => {
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* keep URL only */ }
    setParam("view", v);
  };

  // Optimistic moves: the row shows its new status at once, and is put back
  // with a message if the server refuses.
  const all = useMemo(
    () => (request.data || []).map((t) => (moved[t.id] ? { ...t, status: moved[t.id] } : t)),
    [request.data, moved],
  );
  // Everyone who can own a task, plus anyone who owns one here but has left the team list.
  const owners = useMemo(() => {
    const byId = new Map<number, string>((people.data || []).map((p) => [p.id, p.full_name]));
    for (const t of all) if (t.assigned_to && t.assigned_to_name && !byId.has(t.assigned_to)) byId.set(t.assigned_to, t.assigned_to_name);
    return [...byId].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [people.data, all]);
  const ownerName = owner === "none" ? "Unassigned" : owner ? owners.find((o) => String(o.id) === owner)?.name || "Someone" : "";
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter(
      (t) =>
        (focus === "ideas" ? t.task_type === "idea" : t.task_type !== "idea") &&
        `${cleanTitle(t.title)} ${t.title} ${t.client_name || ""} ${t.assigned_to_name || ""}${isClientRequest(t) ? " client request" : ""}`.toLowerCase().includes(q) &&
        (!owner || (owner === "none" ? !t.assigned_to : String(t.assigned_to) === owner)) &&
        matchesPriority(t, priority) &&
        (view !== "table" || status === "all" || t.status === status) &&
        (focus === 'all' || focus === 'ideas' || (focus === 'mine' ? t.assigned_to === user?.id : focus === 'requests' ? isClientRequest(t) : t.status !== 'completed' && dueBeforeToday(t.due_date))),
    );
  }, [all, query, owner, priority, view, status, focus, user?.id]);

  const clearFilters = () => {
    setQuery('');
    const next = new URLSearchParams(params);
    for (const key of ['focus', 'client_id', 'owner', 'priority']) next.delete(key);
    setParams(next, { replace: true });
  };
  // Search, owner, priority or a client narrow the list; the focus tabs are views, not filters.
  const filtered = !!(query.trim() || owner || priority || clientFilter);
  // The table's Done and All views reach past the 14 days the list loads.
  const olderDoneLink = !olderDone && (view !== "table" || status === "completed" || status === "all");

  async function move(task: TaskRecord, to: string) {
    if (task.status === to) return true;
    if (to === "snoozed") {
      // Snoozing needs a wake-up time, which only the editor asks for.
      open(String(task.id));
      return false;
    }
    setMoved((m) => ({ ...m, [task.id]: to }));
    setNotice("");
    try {
      await api(`/api/tasks/${task.id}`, { method: "PUT", body: JSON.stringify({ status: to }) });
      // Patch in place: useApi.reload() empties the list while it refetches,
      // which would flash the whole board to a spinner on every drag.
      request.setData((list) => list?.map((x) => (x.id === task.id ? { ...x, status: to } : x)) ?? list);
      window.dispatchEvent(new Event("nl-aos-tasks-changed"));
      return true;
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That task could not be moved.");
      return false;
    } finally {
      setMoved((m) => { const n = { ...m }; delete n[task.id]; return n; });
    }
  }
  const toggleDone = (t: TaskRecord) => t.task_type === "idea" ? open(String(t.id)) : move(t, t.status === "completed" ? "pending" : "completed");

  // Inline chip edits (due date, priority, owner): the row shows the new value
  // at once and is put back with a message if the server refuses.
  async function patch(task: TaskRecord, fields: Fields) {
    const before = task;
    request.setData((list) => list?.map((x) => (x.id === task.id ? ({ ...x, ...fields } as TaskRecord) : x)) ?? list);
    setNotice("");
    try {
      const { assigned_to_name: _name, ...body } = fields;
      await api(`/api/tasks/${task.id}`, { method: "PUT", body: JSON.stringify(body) });
      window.dispatchEvent(new Event("nl-aos-tasks-changed"));
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "That change could not be saved.");
      request.setData((list) => list?.map((x) => (x.id === task.id ? before : x)) ?? list);
    }
  }

  const sorted = useMemo(() => {
    const val = (t: TaskRecord): string | number => {
      switch (sort.key) {
        case "title": return cleanTitle(t.title).toLowerCase();
        case "client": return (t.client_name || "").toLowerCase();
        case "owner": return (t.assigned_to_name || "~").toLowerCase();
        case "status": return BOARD.indexOf(t.status);
        case "priority": return PRIORITY_RANK[t.priority] ?? 9;
        case "due": return t.due_date || "9999";
      }
    };
    return [...rows].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * sort.dir);
  }, [rows, sort]);

  const th = (key: typeof sort.key, label: string) => (
    <th aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : 1 }))}>
        {label}
        {sort.key === key && (sort.dir === 1 ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />)}
      </button>
    </th>
  );

  // In the list and the board, a hand-set order wins; tasks nobody has arranged yet
  // sit on top in the sort order above (priority by default), so new work is seen first.
  const groups = BOARD.map((s) => ({
    s,
    items: sorted.filter((t) => t.status === s).map((t, i) => ({ t, i }))
      .sort((a, b) => (a.t.sort_order ?? -1) - (b.t.sort_order ?? -1) || a.i - b.i)
      .map(({ t }) => t),
  }));

  // Drop a task into the list: onto a row (above or below it) or onto the
  // group (its end). Changes status first when it crosses groups, then saves
  // the group's new order.
  async function place(task: TaskRecord, to: string, target: { id: number; after: boolean } | null) {
    const rest = (groups.find((g) => g.s === to)?.items || []).filter((x) => x.id !== task.id);
    if (collapsed[to]) { await move(task, to); return; }
    let at = target ? rest.findIndex((x) => x.id === target.id) : rest.length;
    if (at < 0) at = rest.length;
    else if (target?.after) at += 1;
    const ids = [...rest.slice(0, at).map((x) => x.id), task.id, ...rest.slice(at).map((x) => x.id)];
    const current = groups.find((g) => g.s === to)?.items.map((x) => x.id) || [];
    if (task.status === to && ids.join() === current.join()) return;
    if (task.status !== to && !(await move(task, to))) return;
    const rank = new Map(ids.map((id, i) => [id, i + 1]));
    const before = request.data;
    request.setData((list) => list?.map((x) => (rank.has(x.id) ? { ...x, sort_order: rank.get(x.id) } : x)) ?? list);
    try {
      await api("/api/tasks/order", { method: "PUT", body: JSON.stringify({ ids }) });
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "The new order could not be saved.");
      request.setData((list) => list?.map((x) => ({ ...x, sort_order: before?.find((b) => b.id === x.id)?.sort_order ?? x.sort_order })) ?? list);
    }
  }
  const endDrag = () => { setDragging(null); setDropTarget(null); setRowDrop(null); };

  // Pull the last two days of Gmail (anyone: their own inbox) or, for a superadmin,
  // the last week of Fireflies meetings into the queue.
  async function importMeetings(source: "meetings" | "email" = "meetings") {
    setImporting(true);
    setImported(null);
    try {
      const r = await api<{ message: string }>(`/api/tasks/import-${source}`, { method: "POST", body: JSON.stringify({ days: 7 }) });
      setImported({ tone: "success", text: r.message });
      request.reload();
      window.dispatchEvent(new Event("nl-aos-tasks-changed"));
    } catch (e) {
      setImported({ tone: "danger", text: e instanceof Error ? e.message : source === "email" ? "Gmail could not be read." : "Meetings could not be imported." });
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Work queue"
        actions={
          <div className="header-controls queue-actions">
            {(admin || user?.role === "exec") && (
              <QuickMenu
                label="Import tasks"
                align="right"
                disabled={importing}
                trigger={<span className="button secondary q-import"><Upload aria-hidden="true" /><span className="q-import-label">{importing ? "Importing…" : "Import"}</span><ChevronDown className="q-import-caret" aria-hidden="true" /></span>}
              >
                {(close) => <>
                  {admin && <QuickMenuItem onClick={() => { close(); void importMeetings("meetings"); }}><Mic aria-hidden="true" /> Fireflies meetings, last 7 days</QuickMenuItem>}
                  <QuickMenuItem onClick={() => { close(); void importMeetings("email"); }}><Mail aria-hidden="true" /> {admin ? "Gmail" : "Your Gmail"}, last 2 days</QuickMenuItem>
                </>}
              </QuickMenu>
            )}
            <button className="button" onClick={() => open("new")}>
              <Plus aria-hidden="true" /> {focus === "ideas" ? "New idea" : "New task"}
            </button>
          </div>
        }
      />
      {imported && (
        <p className={`inline-notice ${imported.tone}`} role={imported.tone === "danger" ? "alert" : "status"}>
          <span>{imported.text}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setImported(null)}>×</button>
        </p>
      )}
      <section className="panel queue-panel">
        {/* One control row: quick views, search, filter pills, then the view switch
            and refresh. Phones fold it into two short rows (work.css). */}
        <div className="queue-bar">
          {/* Phones get the short names, so all four fit on one line. */}
          <nav className="work-focus" aria-label="Quick task filters">{[['all',admin?'All work':'My work',admin?'All':'Mine'],...(admin?[['mine','Assigned to me','Mine']]:[]),['overdue','Overdue','Overdue'],['requests','Client requests','Requests'],['ideas','Ideas','Ideas']].map(([id,label,short])=><button key={id} className={focus===id?'active':''} aria-pressed={focus===id} onClick={()=>{const next=new URLSearchParams(params);id==='all'?next.delete('focus'):next.set('focus',id);next.set('status','all');setParams(next);}}>{id==='requests'&&<Inbox aria-hidden="true"/>}<span className="label-long">{label}</span><span className="label-short" aria-hidden="true">{short}</span></button>)}</nav>
          <label className="search-control"><Search aria-hidden="true" /><input aria-label="Search tasks" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search…" /></label>
          {admin && <span className={owner ? "q-pill is-set" : "q-pill"}>
            <FilterPill
              label="Owner"
              value={owner}
              onChange={(v) => setFilter("owner", v || null)}
              options={[
                { value: "", name: "Anyone" },
                { value: "none", name: "Unassigned" },
                ...owners.map((o) => ({ value: String(o.id), name: o.name })),
                ...(owner && owner !== "none" && !owners.some((o) => String(o.id) === owner) ? [{ value: owner, name: ownerName }] : []),
              ]}
            />
            {owner && <button type="button" className="q-pill-clear" aria-label={`Show every owner's tasks, not just ${ownerName}`} title="Clear" onClick={() => setFilter("owner", null)}><X aria-hidden="true" /></button>}
          </span>}
          <span className={priority ? "q-pill is-set" : "q-pill"}>
            <FilterPill
              label="Priority"
              value={priority}
              onChange={(v) => setFilter("priority", v || null)}
              options={[...PRIORITY_OPTIONS, ...(priority && !PRIORITY_OPTIONS.includes(priority) ? [priority] : [])].map((v) => ({
                value: v,
                name: v ? priorityName(v) : "Any",
              }))}
            />
            {priority && <button type="button" className="q-pill-clear" aria-label="Show every priority" title="Clear" onClick={() => setFilter("priority", null)}><X aria-hidden="true" /></button>}
          </span>
          {/* The table shows one status at a time; the list and board show them all as groups. */}
          {view === "table" && (
            <span className="q-pill q-pill-status">
              <FilterPill
                label="Status"
                value={status}
                onChange={(v) => setFilter("status", v)}
                options={[...BOARD.map((value) => ({ value, name: STATUS_LABEL[value], count: all.filter((t) => t.status === value).length })), { value: "all", name: "All", count: all.length }]}
              />
            </span>
          )}
          {filterClientName && (
            <span className="q-pill is-set">
              <span className="filter-pill-face is-set q-client-face"><span className="filter-pill-label">Client</span><span className="filter-pill-value">{filterClientName}</span></span>
              <button type="button" className="q-pill-clear" aria-label={`Show every client's tasks, not just ${filterClientName}`} title="Clear" onClick={() => setFilter("client_id", null)}><X aria-hidden="true" /></button>
            </span>
          )}
          {/* One filter clears with its own ×; several clear together. */}
          {[query.trim(), owner, priority, clientFilter].filter(Boolean).length > 1 && <button className="text-button q-clear" onClick={clearFilters}>Clear filters</button>}
          {/* Every view and status shows its own count; this one is for screen readers as filters change. */}
          <span className="sr-only" aria-live="polite">{request.loading ? "Loading tasks" : `${rows.length} ${rows.length === 1 ? "task" : "tasks"}`}</span>
          {/* The view switch and refresh stay together at the row's end, even when the row wraps. */}
          <div className="q-bar-end">
            <div className="q-views" role="tablist" aria-label="Queue view">
              {([["list", "List", ListIcon], ["table", "Table", Rows3], ["board", "Board", Columns3]] as const).map(([v, label, Icon]) => (
                <button key={v} type="button" role="tab" data-view={v} aria-selected={view === v} aria-label={label} title={label} className={view === v ? "active" : ""} onClick={() => setView(v)}>
                  <Icon aria-hidden="true" /><span className="q-view-label">{label}</span>
                </button>
              ))}
            </div>
            <button className="icon-button q-refresh" aria-label="Refresh tasks" title="Refresh tasks" disabled={request.refreshing} onClick={request.reload}><RefreshCw className={request.refreshing?"spin":""} aria-hidden="true" /></button>
          </div>
        </div>
        {notice && <p role="alert" className="inline-notice danger">{notice}</p>}

        {request.loading && !request.data ? (
          <LoadingState label="Loading tasks" />
        ) : request.error ? (
          <ErrorState message={request.error} onRetry={request.reload} />
        ) : view === "board" ? (
          <div className="q-board">
            {BOARD.map((col) => {
              // The list's order, hand order included, so a task sits in the same place in both views.
              const items = groups.find((g) => g.s === col)?.items || [];
              return (
                <div
                  key={col}
                  className={`q-col${dropTarget === col ? " over" : ""}${items.length ? "" : " is-empty"}`}
                  data-status={col}
                  onDragOver={(e) => { if (dragging !== null) { e.preventDefault(); setDropTarget(col); } }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropTarget(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDropTarget(null);
                    const t = all.find((x) => x.id === dragging);
                    setDragging(null);
                    if (t) void move(t, col);
                  }}
                >
                  <div className="q-col-head"><span className={`q-dot s-${col}`} aria-hidden="true" /><strong>{STATUS_LABEL[col]}</strong><span className="q-count">{items.length}</span></div>
                  <div className="q-col-body">
                    {items.map((t) => (
                      <article
                        key={t.id}
                        className={`q-card${dragging === t.id ? " dragging" : ""}`}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(t.id)); setDragging(t.id); }}
                        onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                        onClick={() => open(String(t.id))}
                        onKeyDown={(e) => { if (e.key === "Enter") open(String(t.id)); }}
                        tabIndex={0}
                        aria-label={`${cleanTitle(t.title)}, ${STATUS_LABEL[t.status]}`}
                      >
                        <span className="q-card-client">{t.client_name || "Agency"}</span>
                        <RequestChip t={t} fit={fits.get(t.id)} />
                        <div className="q-card-top"><CheckButton t={t} onToggle={toggleDone} /><p className={t.status === "completed" ? "q-title done" : "q-title"}>{cleanTitle(t.title)}</p></div>
                        <div className="q-card-meta"><Priority value={t.priority} /><Due task={t} /><Owner name={t.assigned_to_name} /></div>
                      </article>
                    ))}
                    {!items.length && <p className="q-empty">{dragging !== null ? "Drop here" : "Nothing here"}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : !rows.length ? (
          filtered ? (
            <EmptyState title="No matching tasks" description="Try a different search or clear your filters." action={<button className="button secondary" onClick={clearFilters}>Clear filters</button>} />
          ) : focus === "mine" ? (
            <EmptyState title="Nothing is assigned to you" description="Tasks you own show up here." action={<button className="button secondary" onClick={() => setParam("focus", null)}>See all work</button>} />
          ) : focus === "overdue" ? (
            <EmptyState title="Nothing is overdue" description="Tasks past their due date show up here." />
          ) : focus === "ideas" ? (
            <EmptyState title="No ideas yet" description="Keep a thought here, then turn it into a task when it is ready." action={<button className="button" onClick={() => open("new")}><Plus aria-hidden="true" />New idea</button>} />
          ) : focus === "requests" ? (
            <EmptyState title="No client requests yet" description="Clients send requests from their portal, and they land here." />
          ) : (
            view === "table" && status === "completed" && olderDoneLink ? (
              <EmptyState title="Nothing done in the last 14 days" description="Older done tasks load on request." action={<button className="button secondary" onClick={() => setOlderDone(true)}>Show older done tasks</button>} />
            ) : (
              <EmptyState title="No tasks in this view" description={view === "table" ? "Create a task or choose another status." : "Create a task to get started."} action={<button className="button" onClick={() => open("new")}><Plus aria-hidden="true"/>New task</button>} />
            )
          )
        ) : view === "table" ? (
          // A hairline grid (Orbit's table style): every cell bordered, a sticky
          // header, and the same chips as the list so a cell edits in place.
          <div className="q-grid-wrap">
            <table className="q-grid">
              <thead>
                <tr>
                  <th className="q-grid-check"><span className="sr-only">Done</span></th>
                  {th("title", "Task")}{th("client", "Client")}{th("status", "Status")}{th("priority", "Priority")}{th("due", "Due")}{th("owner", "Owner")}
                  <th className="q-grid-go"><span className="sr-only">Open</span></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((t) => (
                  <tr key={t.id} onClick={() => open(String(t.id))} onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) open(String(t.id)); }} tabIndex={0} className={t.status === "completed" ? "done" : ""} aria-label={`Open ${cleanTitle(t.title)}`}>
                    <td className="q-grid-check"><CheckButton t={t} onToggle={toggleDone} /></td>
                    <td className="q-grid-title"><span className={t.status === "completed" ? "q-title done" : "q-title"}>{cleanTitle(t.title)}</span> <RequestChip t={t} fit={fits.get(t.id)} /></td>
                    <td className="q-grid-muted">{t.client_name || "Agency"}</td>
                    <td><StatusChip t={t} onMove={move} /></td>
                    <td><PriorityChip t={t} onPatch={patch} /></td>
                    <td><DueChip t={t} onPatch={patch} /></td>
                    <td><span className="q-grid-owner"><OwnerChip t={t} people={people.data || []} onPatch={patch} /><span className="q-owner-name">{t.assigned_to_name || "Unassigned"}</span></span></td>
                    <td className="q-grid-go"><ChevronRight aria-hidden="true" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="q-grid-foot">
              <span>{sorted.length}</span> {sorted.length === 1 ? "task" : "tasks"}
              {olderDoneLink && <> <button type="button" className="text-button q-older-done-table" onClick={() => setOlderDone(true)}>Show older done tasks</button></>}
            </div>
          </div>
        ) : (
          // The list (Orbit's Tasks list): flush rows under a small group label,
          // one group per status (drag a row to reorder it or onto another group to move it), the circle to complete, the title,
          // then at the row's end (on hover) the status chip, the date, priority and owner, each editing in place.
          <div className="q-flush">
            {/* An empty status says nothing; it shows only while a drag can drop into it. */}
            {groups.filter(({ items }) => items.length || dragging !== null).map(({ s, items }) => (
              <section
                key={s}
                className={`q-flush-group${dropTarget === s ? " over" : ""}`}
                data-status={s}
                onDragOver={(e) => { if (dragging !== null) { e.preventDefault(); setDropTarget(s); } }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) { setDropTarget(null); setRowDrop(null); } }}
                onDrop={(e) => {
                  e.preventDefault();
                  const t = all.find((x) => x.id === dragging);
                  const target = rowDrop;
                  endDrag();
                  if (t) void place(t, s, target);
                }}
              >
                <button type="button" className="q-flush-head" aria-expanded={!collapsed[s]} onClick={() => setCollapsed((c) => ({ ...c, [s]: !c[s] }))}>
                  <ChevronDown aria-hidden="true" className={collapsed[s] ? "closed" : ""} />
                  <span className={`q-dot s-${s}`} aria-hidden="true" />
                  <span className="q-flush-label">{STATUS_LABEL[s]}</span>
                  <span className="q-flush-count">{items.length}</span>
                </button>
                {!collapsed[s] && !items.length && <p className="q-flush-empty">{dragging !== null ? "Drop here" : "No tasks"}</p>}
                {s === "completed" && !collapsed[s] && olderDoneLink && (
                  <button type="button" className="text-button q-older-done" onClick={() => setOlderDone(true)}>Show older done tasks</button>
                )}
                {!collapsed[s] && items.length > 0 && (
                  <div className="q-flush-rows">
                    {items.map((t) => (
                      <div
                        key={t.id}
                        className={`q-flush-row${t.status === "completed" ? " done" : ""}${dragging === t.id ? " dragging" : ""}${rowDrop?.id === t.id ? (rowDrop.after ? " drop-after" : " drop-before") : ""}`}
                        draggable
                        onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", String(t.id)); setDragging(t.id); }}
                        onDragEnd={endDrag}
                        onDragOver={(e) => {
                          if (dragging === null) return;
                          const box = e.currentTarget.getBoundingClientRect();
                          const after = e.clientY > box.top + box.height / 2;
                          const next = dragging === t.id ? null : { id: t.id, after };
                          if (next?.id !== rowDrop?.id || next?.after !== rowDrop?.after) setRowDrop(next);
                        }}
                        role="button"
                        tabIndex={0}
                        aria-label={`Open ${cleanTitle(t.title)}`}
                        onClick={() => open(String(t.id))}
                        onKeyDown={(e) => { if (e.key === "Enter" && e.target === e.currentTarget) open(String(t.id)); }}
                      >
                        <CheckButton t={t} onToggle={toggleDone} />
                        <span className="q-flush-title">
                          <span className={t.status === "completed" ? "q-title done" : "q-title"}>{cleanTitle(t.title)}</span>
                          <span className="q-flush-client">{t.client_name || "Agency"}</span>
                          <RequestChip t={t} fit={fits.get(t.id)} />
                        </span>
                        <span className="q-flush-spacer" aria-hidden="true" />
                        {/* Date, priority and owner sit in fixed slots at the row's end so they line up down the list. */}
                        <span className="q-flush-chips">
                          {/* The group already names the status; the chip shows on hover to move it. */}
                          <span className="q-flush-status"><StatusChip t={t} onMove={move} /></span>
                          <span className="q-flush-due"><DueChip t={t} onPatch={patch} /></span>
                        </span>
                        <span className="q-flush-trail">
                          <PriorityChip t={t} onPatch={patch} />
                          <OwnerChip t={t} people={people.data || []} onPatch={patch} named />
                          <ChevronRight className="q-flush-go" aria-hidden="true" />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </section>
      {params.get("task") && (
        <TaskEditor
          key={params.get("task")}
          id={params.get("task")!}
          defaultClientId={clientFilter}
          defaultKind={focus === "ideas" ? "idea" : "general"}
          onClose={() => open()}
          onSaved={() => {
            open();
            request.reload();
          }}
          onDeleted={(task) => {
            open();
            request.setData((list) => list?.filter((x) => x.id !== task.id) ?? list);
            setImported({ tone: "success", text: `Deleted “${cleanTitle(task.title)}”.` });
          }}
        />
      )}
    </>
  );
}
